import { useMemo } from 'react';
import { Pressable } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { CreateResponsiveStyle, DEVICE_SIZES, maxSize } from 'rn-responsive-styles';
import { useBoolean } from 'usehooks-ts';

import { ContactItem, Modal, ModalProps, spacing } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { useAuthHeaders } from '@pletly/feature-auth';
import { User } from '%__generated__/types';
import { UserNameForPost } from './UserNameForPost';

interface Props extends Omit<ModalProps, 'title'> {
	users?: User[];
}

export const PostSelectedUsersInfo = ({ users }: Props) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation();
	const { getHeaders } = useAuthHeaders();
	const { value: isModalVisible, setTrue: openModal, setFalse: closeModal } = useBoolean();
	const styles = useStyles();

	const [firstUser] = users || [];

	const firstUserText = useMemo(
		() => (
			<Pressable style={styles.name} onPress={() => navigate('Profile', { userId: firstUser?.id })}>
				<UserNameForPost>{getName(firstUser)}</UserNameForPost>
			</Pressable>
		),
		[styles.name, firstUser, navigate],
	);

	if (!users || users.length === 0) {
		return null;
	}

	if (users.length < 2) {
		return firstUserText;
	}

	const notVisibleUsersCount = users.length - 1;

	return (
		<>
			<Pressable style={styles.nameAndCountContainer} onPress={openModal}>
				{firstUserText}
				<UserNameForPost>{`+${notVisibleUsersCount}`}</UserNameForPost>
			</Pressable>

			<Modal title={t('post.selected-users')} visible={isModalVisible} onClose={closeModal}>
				{users.map(({ profilePhoto, uuid, ...userInfo }) => (
					<ContactItem
						key={uuid}
						name={getName(userInfo)}
						photoSource={
							profilePhoto
								? {
										uri: profilePhoto.url,
										headers: getHeaders(profilePhoto.url),
									}
								: undefined
						}
					/>
				))}
			</Modal>
		</>
	);
};

const useStyles = CreateResponsiveStyle(
	{
		nameAndCountContainer: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: spacing(),
		},
		name: {},
	},
	{
		[maxSize(DEVICE_SIZES.SMALL_DEVICE)]: {
			name: { maxWidth: 220 },
		},
	},
);
